import { useState, type ComponentProps } from "react";
import { Plus, Trash2, Server } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BrokerForm } from "./BrokerForm";
import { BrokerOutputList } from "./BrokerOutputList";
import {
  useBrokers,
  useCreateBroker,
  useDeleteBroker,
} from "@/hooks/useBrokers";
import { BROKER_TYPE_LABELS } from "@production-hub/shared";
import type { BrokerType } from "@production-hub/shared";
import type { CloudBroker } from "@/hooks/useBrokers";

interface BrokerListProps {
  studioId: string;
}

export const BrokerList = ({ studioId }: BrokerListProps) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteBrokerId, setDeleteBrokerId] = useState<string | null>(null);

  const { data: brokers = [], isLoading, isError } = useBrokers(studioId);
  const createBroker = useCreateBroker();
  const deleteBroker = useDeleteBroker();

  const handleCreate: ComponentProps<typeof BrokerForm>["onSubmit"] = (
    data,
  ) => {
    createBroker.mutate(
      { ...data, studio_id: studioId },
      { onSuccess: () => setIsCreateDialogOpen(false) },
    );
  };

  const handleDelete = () => {
    if (!deleteBrokerId) return;
    deleteBroker.mutate(
      { id: deleteBrokerId, studioId },
      { onSuccess: () => setDeleteBrokerId(null) },
    );
  };

  if (isError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8 text-destructive">
          <Server className="h-8 w-8 mb-2" />
          <p>Failed to load brokers</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Distribution Brokers</h3>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Broker
        </Button>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Loading brokers...
          </CardContent>
        </Card>
      ) : brokers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Server className="h-8 w-8 mb-2" />
            <p>No brokers configured</p>
            <p className="text-sm">Add a broker to distribute streams</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {brokers.map((broker) => (
            <BrokerCard
              key={broker.id}
              broker={broker}
              onDelete={() => setDeleteBrokerId(broker.id)}
            />
          ))}
        </div>
      )}

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Broker</DialogTitle>
          </DialogHeader>
          <BrokerForm
            onSubmit={handleCreate}
            onCancel={() => setIsCreateDialogOpen(false)}
            isLoading={createBroker.isPending}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteBrokerId}
        onOpenChange={(open) => !open && setDeleteBrokerId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Broker?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this broker and all its outputs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

interface BrokerCardProps {
  broker: CloudBroker;
  onDelete: () => void;
}

const BrokerCard = ({ broker, onDelete }: BrokerCardProps) => {
  const config =
    typeof broker.config === "string"
      ? JSON.parse(broker.config)
      : broker.config || {};
  const brokerType = broker.broker_type as BrokerType;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base">{broker.name}</CardTitle>
            <p className="text-sm text-muted-foreground">
              {BROKER_TYPE_LABELS[brokerType] || broker.broker_type}
            </p>
          </div>
          <Badge
            variant={broker.enabled ? "default" : "outline"}
            className={
              broker.enabled ? "bg-green-600" : "text-muted-foreground"
            }
          >
            {broker.enabled ? "Enabled" : "Disabled"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {config.host && (
          <div className="flex justify-between text-sm mb-1">
            <span className="text-muted-foreground">Host</span>
            <span className="font-mono">
              {config.host}
              {config.port ? `:${config.port}` : ""}
            </span>
          </div>
        )}
        {config.region && (
          <div className="flex justify-between text-sm mb-1">
            <span className="text-muted-foreground">Region</span>
            <span>{config.region}</span>
          </div>
        )}

        <BrokerOutputList brokerId={broker.id} />

        <div className="flex gap-2 mt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-destructive hover:text-destructive ml-auto"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
